import { hasClaim } from '../auth/authHelpers';
import bus from '../connection/storeManagerConnection';
import endpoint from '../util/endpoint';
import { clearMhd, dequeueMhdItem, enqueueMhdItem, mhdReady, mhdStore, setMhdItem, type StoredMhdItem } from './mhd.store';
import { updateProduct } from '@/product/product.store';
import { ConnectionStatus } from '@whiz-cart/node-shared/connection/connection';
import sleep from '@whiz-cart/node-shared/sleep';
// @ts-expect-error missing types
import { SwSync } from '@whiz-cart/ui-shared/sync/swSync.js';
import _ from 'lodash';

const swSync = new SwSync('mhd');

const transformIncoming = <T extends (any | undefined)[]>(...items: T): { [K in keyof T]: any } => {
    const productsUi: any[] = [];

    const transformed = items.map(({ barcode, expireOn, amount, updatedOn, updatedBy, productUi, mhdGroup, unknownProduct }) => {
        if (productUi) {
            productsUi.push({ ...productUi, mhdGroup });
        }
        return {
            id: `${bus.config.storeGuid}:${barcode}`,
            storeGuid: bus.config.storeGuid,
            barcode,
            expireOn,
            amount,
            updatedOn,
            updatedBy,
            mhdGroup: unknownProduct ? unknownProduct.mhdGroup : mhdGroup,
        };
    }) as { [K in keyof T]: any };

    if (productsUi.length) {
        updateProduct(bus.config.storeGuid ?? '', ...productsUi);
    }

    return transformed;
};

const transformOutgoing = ({ barcode, expireOn, amount }: StoredMhdItem) => ({
    storeGuid: bus.config.storeGuid,
    barcode,
    expireOn,
    amount,
    updatedBy: mhdStore.get().deviceId,
});

const pull = async (): Promise<StoredMhdItem[]> => {
    if (!bus.config.storeGuid) return [];

    if (!hasClaim()('product/expiration/read', 'store', bus.config.storeGuid)) return [];

    const maxDate =
        _(mhdStore.get().items)
            .values()
            .filter((item) => item.storeGuid === bus.config.storeGuid)
            .map((item) => new Date(item.updatedOn!))
            .filter((date) => !isNaN(date.getTime()))
            .max() || new Date(0);

    return endpoint('storeManager.getMHDList', { storeGuid: bus.config.storeGuid })
        .post({ from: maxDate.toISOString() })
        .then((items: any) => transformIncoming(...items));
};

function receiveItem(incoming: StoredMhdItem) {
    const local = mhdStore.get().items[incoming.id];

    const isEcho = local && !local.updatedBy && incoming.updatedBy === mhdStore.get().deviceId;
    const isNewer = !local?.updatedOn || new Date(local.updatedOn) < new Date(incoming.updatedOn!);

    if (isNewer && !isEcho) setMhdItem(incoming);
    // Our own update coming back: keep the local values, which may have changed again in the meantime
    else if (isNewer) setMhdItem({ ...local, updatedOn: incoming.updatedOn });

    console.debug(`${!isNewer ? 'Ignored' : isEcho ? 'Merged' : 'Applied'} incoming item (isNewer=${isNewer}, isEcho=${isEcho}):`, {
        local,
        incoming,
    });
}

async function restore() {
    try {
        for (const item of _.orderBy(await pull(), (item) => item.updatedOn)) receiveItem(item);
    } catch (error) {
        console.error('Could not fetch updates', error);
    }
}

let isPushing = false;

async function pushQueue() {
    if (isPushing) return;
    isPushing = true;
    try {
        let item;
        while ((item = mhdStore.get().queue[0])) {
            try {
                await swSync.queue(transformOutgoing(item));
            } catch (error: any) {
                console.error('Error when pushing queue item', error);
                if (!error.response || error.response.status === 500) {
                    await sleep(1000);
                    continue;
                }
            }
            dequeueMhdItem();
        }
    } finally {
        isPushing = false;
    }
}

/** Applies an update locally right away and queues it for delivery through the service worker. */
function updateItem(item: StoredMhdItem) {
    item = { ...item, updatedBy: undefined };
    setMhdItem(item);
    enqueueMhdItem(item);
    void mhdReady.then(pushQueue);
    console.debug('Updated item locally:', item);
}

function clear() {
    clearMhd();
    console.debug('Cleared sync data.');
}

void mhdReady.then(() => {
    bus.subscribe('productExpirationUpdate', (item) => {
        // TODO: parse item
        receiveItem(transformIncoming(item)[0]);
    });
    bus.subscribeStatus((status) => status === ConnectionStatus.Connected && void restore());
    void restore();
    void pushQueue();
});

export default { updateItem, clear };
